import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { MsalProvider } from "@azure/msal-react";
import { broadcastResponseToMainFrame } from "@azure/msal-browser/redirect-bridge";
import { msalInstance } from "@/authConfig";
import "@/index.css";
import App from "@/App";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      refetchOnWindowFocus: true,
    },
  },
});

// MSAL Browser v5 popup/silent flows return to the redirect URI (our app root)
// inside a popup or hidden iframe. That page is a "redirect bridge": it must hand
// the auth response to the main window over a BroadcastChannel and close itself.
// Rendering the app here (or calling handleRedirectPromise) leaves the popup blank
// and stuck, because it consumes the response before the opener can read it.
const hasAuthResponseInUrl = () =>
  /(?:^|[#?&])(code|state|error)=/.test(window.location.hash) ||
  /(?:^|[?&])(code|state|error)=/.test(window.location.search);

async function bootstrap() {
  if (hasAuthResponseInUrl()) {
    try {
      await broadcastResponseToMainFrame();
    } catch (e) {
      console.error("MSAL redirect bridge failed:", e);
    }
    return; // never render the app on the auth-response (popup/iframe) page
  }

  await msalInstance.initialize();

  try {
    const result = await msalInstance.handleRedirectPromise();
    if (result?.account) msalInstance.setActiveAccount(result.account);
  } catch {
    /* ignore */
  }

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  const root = ReactDOM.createRoot(document.getElementById("root"));
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <MsalProvider instance={msalInstance}>
          <App />
        </MsalProvider>
      </QueryClientProvider>
    </React.StrictMode>,
  );
}

bootstrap();
